import { useMutation } from '@tanstack/react-query';
import { fetchExperience } from 'flux/clients/experiences';
import { toggleFavorite } from 'flux/clients/favorites';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import { FavoritesContext } from 'providers/FavoritesProvider';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { generatePathnameWithLangCode, getPath } from 'utils/RouteHelper';
import { useCurrencyContext } from '~/context/CurrencyContext';
import { useI18n } from '~/stores/i18n';

function useFavorites(id) {
  const { state } = useCurrencyContext();
  const { favorites, dispatch } = useContext(FavoritesContext);
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const { lang } = useI18n();

  function onSuccess() {
    navigate(location, { replace: true });
  }

  function updateFavoriteContext(newFavoriteState: boolean) {
    const newFavorites = favorites.filter(
      (favorite) => favorite && favorite.id !== id,
    );
    if (newFavoriteState) {
      newFavorites.push({ id });
    }
    dispatch({ type: 'SET', payload: { favorites: newFavorites } });
    fetchExperience(id, lang, state.currency).then((experience) =>
      dispatch({ type: 'SET_EXPERIENCE', payload: { experience } }),
    );
  }

  function onError(error: any) {
    if (error.status === 401) {
      updateFavoriteContext(false);
      const searchParams = new URLSearchParams();
      searchParams.set(
        'returnTo',
        `/favorites/add/${id}/?returnTo=${getPath()}`,
      );

      navigate(
        {
          pathname: generatePathnameWithLangCode('/signup/', lang),
          search: searchParams.toString(),
        },
        { state: { modal: true } },
      );
    }
  }

  function onMutate(newFavoriteState: boolean) {
    updateFavoriteContext(newFavoriteState);
  }

  const mutate = useMutation(
    async (isFavorite: boolean) => {
      await toggleFavorite(id, isFavorite);
      return isFavorite;
    },
    {
      retry: 0,
      onSuccess: onSuccess,
      onError: onError,
      onMutate: onMutate,
    },
  );

  const setAsFavorite = (isWished: boolean) => {
    mutate.mutate(isWished);
  };

  return {
    setAsFavorite,
    isFavorite: favorites.some((favorite) => favorite && favorite.id === id),
    isLoading: mutate.isLoading,
  };
}

export default useFavorites;
